import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { LoaderComponent, TableActionButton } from 'app/shared/components';
import { FiEdit2 } from 'react-icons/fi';
import { useDebounce, useService } from 'app/shared/hooks';
import { buscaPerguntas, buscaPerguntasCount } from 'app/services/pergunta';
import { buscaSecoesPorGenero, buscaCategoriaPergunta } from 'app/services/serviceCategoria';
import { TextCounter } from '../style';
import CreateMovimentoModal from '../../CreateMovimentoModal';
import QuestionCard from '../../QuestionCard';
import CreateEstrategiaModal from '../../CreateEstrategiaModal';
import EditMovimentoModal from '../../EditMovimentoModal';
import OrderMovimentosModal from '../../OrderMovimentosModal';
import OrderEstrategiasModal from '../../OrderEstrategiasModal';
import CreateSecaoModal from '../../CreateSecaoModal.jsx';
import EditSecaoModal from '../../EditSecaoModal';
import OrderSecoesModal from '../../OrderSecoesModal';

const TabMovimento = ({ generoTextual, updateGenero }) => {
  const [secoes, setSecoes] = useState([]);
  const [secaoSelecionada, setSecaoSelecionada] = useState(null);
  const [openCreateSecaoModal, setOpenCreateSecaoModal] = useState(false);
  const [openEditSecaoModal, setOpenEditSecaoModal] = useState(false);
  const [openCreateMovimentoModal, setOpenCreateMovimentoModal] = useState(false);
  const [openOrderSecoesModal, setOpenOrderSecoesModal] = useState(false);
  const [openOrderMovimentosModal, setOpenOrderMovimentosModal] = useState(false);
  const [openOrderEstrategiasModal, setOpenOrderEstrategiasModal] = useState(false);
  const [openEditMovimentoModal, setOpenEditMovimentoModal] = useState(false);
  const [categoriaPergunta, setCategoriaPergunta] = useState(null);
  const [categoriaPerguntaSearch, setCategoriaPerguntaSearch] = useState('');
  const [openCreateEstrategiaModal, setOpenCreateEstrategiaModal] = useState(false);
  const categoriaPerguntaSearchDebounced = useDebounce(categoriaPerguntaSearch);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    request: requestBuscaSecoesPorGenero,
    response: buscaSecoesPorGeneroResponse,
    retry: retryBuscaSecoesPorGenero,
  } = useService(buscaSecoesPorGenero, {
    cache: true,
    cacheKey: 'buscaSecoes',
  });

  const {
    request: requestBuscaCategoriasPergunta,
    response: buscaCategoriasPerguntaResponse,
    retry,
  } = useService(buscaCategoriaPergunta, {
    cache: true,
    cacheKey: 'buscaMovimentos',
  });

  const {
    request: requestBuscaPerguntas,
    response: buscaPerguntasResponse,
    retry: retryBuscaPerguntas,
  } = useService(buscaPerguntas, {
    cache: true,
    cacheKey: 'buscaPerguntas',
  });

  const {
    request: requestBuscaPerguntasCount,
    response: buscaPerguntasCountResponse,
    retry: retryBuscaPerguntasCount,
  } = useService(buscaPerguntasCount);

  const handleBuscaMovimentos = useCallback((ignoreCache = false) => {
    if (!secaoSelecionada?.id || buscaCategoriasPerguntaResponse.loading) {
      return;
    }

    if (ignoreCache) {
      updateGenero();
    }

    requestBuscaCategoriasPergunta(
      {
        limit: 50,
        page: 0,
        nome: categoriaPerguntaSearchDebounced,
        genero_textual_id: generoTextual.id,
        secao_categoria_id: secaoSelecionada.id,
      },
      { ignoreCache }
    );
  }, [secaoSelecionada, categoriaPerguntaSearchDebounced, generoTextual, updateGenero, requestBuscaCategoriasPergunta, buscaCategoriasPerguntaResponse.loading]);

  const handleBuscaPerguntas = useCallback((ignoreCache = false) => {
    if (!categoriaPergunta?.id) {
      return;
    }

    if (buscaPerguntasResponse.loading) {
      return;
    }

    requestBuscaPerguntas(
      {
        genero_textual_id: generoTextual.id,
        categoria_pergunta_id: categoriaPergunta.id,
        with: "itens",
        limit: 100,
      },
      {
        ignoreCache,
      },
    );
  }, [generoTextual, categoriaPergunta, requestBuscaPerguntas, buscaPerguntasResponse.loading]);

  const handleGetBuscaPerguntas = useCallback(() => {
    requestBuscaPerguntasCount({
      genero_textual_id: generoTextual.id,
    });

    if (categoriaPergunta?.id) {
      handleBuscaPerguntas(true);
    }
  }, [generoTextual, categoriaPergunta, requestBuscaPerguntasCount, handleBuscaPerguntas]);

  useEffect(() => {
    if (generoTextual?.id) {
      requestBuscaSecoesPorGenero({ genero_textual_id: generoTextual.id });
    }
  }, [generoTextual]);

  useEffect(() => {
    if (buscaSecoesPorGeneroResponse?.data?.data) {
      const fetchedSecoes = buscaSecoesPorGeneroResponse.data.data.map((item) => {
        return {
          id: item.id,
          label: item.nome,
          nome: item.nome,
          descricao: item.descricao,
        };
      });
      setSecoes(fetchedSecoes);

      if (!secaoSelecionada || !fetchedSecoes.some(s => s.id === secaoSelecionada.id)) {
        if (fetchedSecoes.length > 0) {
          setSecaoSelecionada(fetchedSecoes[0]);
        } else {
          setSecaoSelecionada(null);
        }
      }
    }
  }, [buscaSecoesPorGeneroResponse]);

  useEffect(() => {
    if (secaoSelecionada) {
      handleBuscaMovimentos();
    }
  }, [categoriaPerguntaSearchDebounced, secaoSelecionada, handleBuscaMovimentos]);

  useEffect(() => {
    if (categoriaPergunta) {
      handleBuscaPerguntas();
    }
  }, [categoriaPergunta, handleBuscaPerguntas]);

  useEffect(() => {
    if (buscaCategoriasPerguntaResponse?.data?.data) {
      const fetchedCategorias = buscaCategoriasPerguntaResponse.data.data.map((item) => {
        return {
          id: item.id,
          label: item.nome,
          nome: item.nome,
          descricao: item.descricao,
          cor: item.cor,
          secao_categoria_id: secaoSelecionada?.id,
        };
      }) || [];

      if (!categoriaPergunta || !fetchedCategorias.some(c => c.id === categoriaPergunta.id)) {
        if (fetchedCategorias.length > 0) {
          setCategoriaPergunta(fetchedCategorias[0]);
        } else {
          setCategoriaPergunta(null);
        }
      }
    }
  }, [buscaCategoriasPerguntaResponse]);

  const totalPerguntas = generoTextual.perguntas_count;
  const totalCategoriasPergunta = generoTextual.categorias_pergunta_count;
  const categoriasPergunta =
    buscaCategoriasPerguntaResponse?.data?.map((item) => {
      return {
        id: item.id,
        label: item.nome,
        nome: item.nome,
        descricao: item.descricao,
        cor: item.cor,
        secao_categoria_id: secaoSelecionada?.id,
      };
    }) || [];

  const perguntas = buscaPerguntasResponse?.data?.data || [];
  const perguntasTotal = buscaPerguntasResponse?.data?.total || 0;

  const secoesOrdenacao = secoes.map((item, index) => ({
    id: item.id,
    title: `Seção ${index + 1}`,
    subtitle: item.nome,
    ordem: item.ordem,
  }));

  const movimentosOrdenacao = buscaCategoriasPerguntaResponse?.data?.map((item, key) => {
    return {
      id: item.id,
      title: `Movimento ${key + 1}`,
      subtitle: item.nome,
      ordem: item.ordem,
    };
  });

  const estrategiasOrdenacao = buscaPerguntasResponse?.data?.data?.map((item, key) => {
    return {
      id: item.id,
      title: `Estratégia ${key + 1}`,
      subtitle: item.titulo,
      ordem: item.ordem,
    };
  });

  return (
    <Grid2>
      <LoaderComponent
        message="Buscando seções e movimentos de gênero textual..."
        errorMessage="Falha ao buscar seções e movimentos de gênero textual"
        {...buscaCategoriasPerguntaResponse}
        retry={retry}
      >
        <Grid2 size={12} mt={2} container justifyContent={'space-between'} flexDirection={'row'}>
          <Grid2 alignSelf={'center'}>
            <Button
              style={{ minWidth: 180 }}
              onClick={() => setOpenCreateSecaoModal(true)}
              variant="contained"
            >
              Nova seção
            </Button>
            <Button
              style={{
                minWidth: 180,
                marginLeft: isMobile ? 0 : 10,
                marginTop: isMobile ? 8 : 0,
                marginBottom: isMobile ? 10 : 0,
              }}
              onClick={() => {
                setOpenOrderSecoesModal(true);
              }}
              variant="contained"
            >
              Ordenar seções
            </Button>
          </Grid2>
          <Grid2 alignSelf={'center'}>
            <TextCounter>Total de seções: {secoes.length}</TextCounter>
            <TextCounter
              sx={{
                textAlign: 'left',
              }}
            >
              Total de movimentos: {totalCategoriasPergunta}
            </TextCounter>
            <TextCounter
              sx={{
                textAlign: 'left',
              }}
            >
              Total de passos: {totalPerguntas}
            </TextCounter>
          </Grid2>
        </Grid2>

        {/* Autocomplete para seleção de Seção */}
        <Grid2 size={12} mt={2}>
          <Grid2 size={12} flexDirection={'row'} container>
            <FormControl
              style={{
                marginLeft: 0,
                width: secaoSelecionada ? (isMobile ? '80%' : '95%') : '100%',
              }}
            >
              <Autocomplete
                disablePortal
                options={secoes}
                fullWidth
                key={secaoSelecionada?.id}
                value={secaoSelecionada}
                onChange={(e, newValue) => {
                  e.preventDefault();
                  setSecaoSelecionada(newValue);
                  setCategoriaPergunta(null);
                }}
                renderInput={(params) => (
                  <TextField
                    required
                    slotProps={
                      buscaSecoesPorGeneroResponse.loading
                        ? {
                          input: {
                            endAdornment: (
                              <CircularProgress
                                sx={{ position: 'absolute', right: '10px' }}
                                size="20px"
                              />
                            ),
                          },
                        }
                        : undefined
                    }
                    {...params}
                    style={{ margin: 0 }}
                    placeholder="Seção selecionada"
                  />
                )}
              />
            </FormControl>
            {secaoSelecionada && (
              <TableActionButton
                style={{ marginLeft: 10, verticalAlign: 'middle', marginTop: 5 }}
                onClick={() => setOpenEditSecaoModal(true)}
              >
                <FiEdit2 />
              </TableActionButton>
            )}
          </Grid2>
        </Grid2>

        {secaoSelecionada && (
          <Grid2 size={12} mt={2} container justifyContent={'space-between'} flexDirection={'row'}>
            <Grid2 alignSelf={'center'}>
              <Button
                style={{ minWidth: 180 }}
                onClick={() => setOpenCreateMovimentoModal(true)}
                variant="contained"
              >
                Novo movimento
              </Button>
              <Button
                style={{
                  minWidth: 180,
                  marginLeft: isMobile ? 0 : 10,
                  marginTop: isMobile ? 8 : 0,
                  marginBottom: isMobile ? 10 : 0,
                }}
                onClick={() => {
                  setOpenOrderMovimentosModal(true);
                }}
                variant="contained"
              >
                Ordenar movimentos
              </Button>
            </Grid2>
          </Grid2>
        )}

        {/* Autocomplete para seleção de Movimento */}
        {secaoSelecionada && (
          <Grid2 size={12} mt={2}>
            <Grid2 size={12} flexDirection={'row'} container>
              <FormControl
                style={{
                  marginLeft: 0,
                  width: categoriaPergunta ? (isMobile ? '80%' : '95%') : '100%',
                }}
              >
                <Autocomplete
                  disablePortal
                  options={categoriasPergunta}
                  fullWidth
                  key={categoriaPergunta?.id}
                  value={categoriaPergunta}
                  onChange={(e, newValue) => {
                    e.preventDefault();
                    setCategoriaPergunta(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      required
                      slotProps={
                        buscaCategoriasPerguntaResponse.loading
                          ? {
                            input: {
                              endAdornment: (
                                <CircularProgress
                                  sx={{ position: 'absolute', right: '10px' }}
                                  size="20px"
                                />
                              ),
                            },
                          }
                          : undefined
                      }
                      {...params}
                      style={{ margin: 0 }}
                      placeholder="Movimento selecionado"
                      onChange={(e) => {
                        setCategoriaPerguntaSearch(e.target.value);
                      }}
                    />
                  )}
                />
              </FormControl>
              {categoriaPergunta && (
                <TableActionButton
                  style={{ marginLeft: 10, verticalAlign: 'middle', marginTop: 5 }}
                  onClick={() => setOpenEditMovimentoModal(true)}
                >
                  <FiEdit2 />
                </TableActionButton>
              )}
            </Grid2>

            {categoriaPergunta && (
              <small
                style={{
                  fontFamily: 'Roboto',
                  fontSize: '12px',
                  fontWeight: 400,
                }}
              >
                Total de Passos do movimento: {perguntasTotal}
              </small>
            )}
          </Grid2>
        )}
      </LoaderComponent>

      <LoaderComponent
        message="Buscando passos gênero textual..."
        errorMessage="Falha ao buscar passos de gênero textual"
        {...buscaPerguntasResponse}
        retry={retryBuscaPerguntas}
      >
        {categoriaPergunta && !buscaPerguntasResponse.loading && (
          <Grid2 container justifyContent={'space-between'} flexDirection={'row'} mt={2}>
            <Grid2>
              <Button
                style={{ minWidth: 180 }}
                onClick={() => {
                  setOpenCreateEstrategiaModal(true);
                }}
                variant="contained"
              >
                adicionar estratégia
              </Button>
            </Grid2>
            <Grid2>
              <Button
                style={{ minWidth: 180 }}
                onClick={() => {
                  setOpenOrderEstrategiasModal(true);
                }}
                variant="contained"
                sx={{ mt: 1 }}
              >
                ordenar passos
              </Button>
            </Grid2>
          </Grid2>
        )}
      </LoaderComponent>

      {categoriaPergunta && (
        <Grid2 size={12} container mt={2}>
          {perguntas?.map((item) => (
            <QuestionCard
              pergunta={item}
              key={item.id}
              titulo={item.titulo}
              isRequired={item.obrigatorio === '1'}
              isFinalText={item.resposta_texto_final === '1'}
              onSuccess={handleGetBuscaPerguntas}
            />
          ))}
        </Grid2>
      )}

      <CreateSecaoModal
        open={openCreateSecaoModal}
        onClose={() => setOpenCreateSecaoModal(false)}
        generoTextual={generoTextual}
        onSuccess={(data) => {
          const newSelected = {
            id: data.id,
            label: data.nome,
            nome: data.nome,
            descricao: data.descricao,
          };
          setSecaoSelecionada(newSelected);
          setCategoriaPergunta(null);
          requestBuscaSecoesPorGenero({ genero_textual_id: generoTextual.id }, { ignoreCache: true });
        }}
      />

      <CreateMovimentoModal
        open={openCreateMovimentoModal}
        onClose={() => setOpenCreateMovimentoModal(false)}
        generoTextual={generoTextual}
        secaoCategoriaId={secaoSelecionada?.id}
        onSuccess={(data) => {
          const newSelected = {
            id: data.id,
            label: data.nome,
            nome: data.nome,
            descricao: data.descricao,
            cor: data.cor,
            secao_categoria_id: secaoSelecionada.id,
          };
          setCategoriaPergunta(newSelected);
          handleBuscaMovimentos(true);
        }}
      />

      {categoriaPergunta && (
        <CreateEstrategiaModal
          open={openCreateEstrategiaModal}
          movimentoId={categoriaPergunta.id}
          movimentoNome={categoriaPergunta.nome}
          onClose={() => setOpenCreateEstrategiaModal(false)}
          onSuccess={() => handleGetBuscaPerguntas()}
          generoTextualId={generoTextual.id}
          perguntaCount={perguntasTotal + 1}
        />
      )}

      {secaoSelecionada && (
        <EditSecaoModal
          open={openEditSecaoModal}
          onClose={() => setOpenEditSecaoModal(false)}
          secao={secaoSelecionada}
          onSuccess={(data) => {
            const updatedSecao = {
              id: data.id,
              label: data.nome,
              nome: data.nome,
              descricao: data.descricao,
            };
            setSecaoSelecionada(updatedSecao);
            requestBuscaSecoesPorGenero({ genero_textual_id: generoTextual.id }, { ignoreCache: true });
          }}
        />
      )}

      {categoriaPergunta && (
        <EditMovimentoModal
          open={openEditMovimentoModal}
          onClose={() => setOpenEditMovimentoModal(false)}
          movimentoId={categoriaPergunta.id}
          nome={categoriaPergunta.nome}
          descricao={categoriaPergunta.descricao}
          defaultColor={categoriaPergunta.cor}
          secaoCategoriaId={categoriaPergunta.secao_categoria_id}
          onSuccess={(data) => {
            const newSelected = {
              id: data.id,
              label: data.nome,
              nome: data.nome,
              descricao: data.descricao,
              cor: data.cor,
              secao_categoria_id: secaoSelecionada?.id,
            };
            setCategoriaPergunta(newSelected);
            handleBuscaMovimentos(true);
          }}
        />
      )}

      {secoesOrdenacao && (
        <OrderSecoesModal
          open={openOrderSecoesModal}
          onClose={() => setOpenOrderSecoesModal(false)}
          secoes={secoesOrdenacao}
          onSuccess={() => {
            requestBuscaSecoesPorGenero({ genero_textual_id: generoTextual.id }, { ignoreCache: true });
          }}
        />
      )}


      {movimentosOrdenacao && (
        <OrderMovimentosModal
          onClose={() => setOpenOrderMovimentosModal(false)}
          open={openOrderMovimentosModal}
          movimentos={movimentosOrdenacao}
          onSuccess={() => handleBuscaMovimentos(true)}
        />
      )}

      {categoriaPergunta && estrategiasOrdenacao && (
        <OrderEstrategiasModal
          onClose={() => setOpenOrderEstrategiasModal(false)}
          open={openOrderEstrategiasModal}
          estrategias={estrategiasOrdenacao}
          movimentoTitulo={categoriaPergunta.nome}
          onSuccess={() => handleBuscaPerguntas(true)}
        />
      )}
    </Grid2>
  );
};

export default TabMovimento;